// src/screen/dashboard/ExplorerDashboard.tsx
import React, { useState, useEffect } from "react";
import { FileItem } from "../../types/FileItem";
import { Menu, MenuItem, TextField } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import FolderIcon from "@mui/icons-material/Folder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { fileService } from "../../services/fileService";
import "./dashboard.css";
import { fileActions } from "../../services/fileActions";
import { LoginService } from "../../services/loginService";
import { useRef } from "react";
import { Box, Typography, LinearProgress, Paper, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const ExplorerDashboard: React.FC = () => {
  const [rootData, setRootData] = useState<FileItem[]>([]);
  const [currentPath, setCurrentPath] = useState<FileItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<FileItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<FileItem | null>(null);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const normalizeFiles = (items: any[], parentId = ""): FileItem[] => {
    return items.map((item, index) => {
      const id = parentId + index;
      return {
        id,
        name: item.name,
        path: item.path,
        type: item.type,
        parentId,
        size: item.size ?? undefined,
        lastModified: item.lastModified ?? undefined,
        children: item.type === "folder" ? [] : undefined,
        isVirtual: false,
      };
    });
  };
  
  //DAta Access
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const data = await fileService.list(); // await the API call
        const normalized = normalizeFiles(data);
        setRootData(normalized);
        setCurrentPath(normalized);
      } catch (err) {
        console.error("Failed to fetch files:", err);
      }
    };

    fetchFiles();
  }, []);
  // ================================
  // Helper functions for tree update
  // ================================

  const buildBreadcrumbs = (id: string, items: FileItem[]): FileItem[] => {
    const findParent = (id: string, items: FileItem[]): FileItem | null => {
      for (const item of items) {
        if (item.id === id) return item;
        if (item.children) {
          const found = findParent(id, item.children);
          if (found) return found;
        }
      }
      return null;
    };

    const breadcrumb: FileItem[] = [];
    let currentId: string | undefined = id;

    while (currentId) {
      const currentItem = findParent(currentId, items);
      if (!currentItem) break;
      breadcrumb.unshift(currentItem); // prepend to start
      currentId = currentItem.parentId;
    }

    return breadcrumb;
  };

  
  const updateItemName = (items: FileItem[], id: string, newName: string): FileItem[] => {
    return items.map((item) => {
      if (item.id === id) return { ...item, name: newName };
      if (item.children) return { ...item, children: updateItemName(item.children, id, newName) };
      return item;
    });
  };

  const deleteItemById = (items: FileItem[], id: string): FileItem[] => {
    return items
      .filter((item) => item.id !== id)
      .map((item) => ({
        ...item,
        children: item.children ? deleteItemById(item.children, id) : undefined,
      }));
  };

  const findItemById = (items: FileItem[], id: string): FileItem | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const addItemToCurrentPath = (items: FileItem[], path: FileItem[], newItem: FileItem): FileItem[] => {
    if (path.length === 0) return [...items, newItem];
    const [head, ...rest] = path;
    return items.map((item) => {
      if (item.id === head.id) {
        return { ...item, children: addItemToCurrentPath(item.children || [], rest, newItem) };
      }
      return item;
    });
  };

  const addItemsToCurrentPath = (items: FileItem[], path: FileItem[], newItems: FileItem[]): FileItem[] => {
    if (path.length === 0) return [...items, ...newItems];
    const [head, ...rest] = path;
    return items.map((item) => {
      if (item.id === head.id) {
        return { ...item, children: addItemsToCurrentPath(item.children || [], rest, newItems) };
      }
      return item;
    });
  };

  // ================================
  // Breadcrumb navigation
  // ================================
  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setCurrentPath(rootData);
      setBreadcrumbs([]);
    } else {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);
      setCurrentPath(newBreadcrumbs[index].children || []);
    }
  };

  // ================================
  // Context menu
  // ================================
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>, item?: FileItem) => {
    event.preventDefault();
    if (item) setSelectedItem(item);
    else setSelectedItem(null);
    setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
  };

  const handleClose = () => setContextMenu(null);

  // ================================
  // Actions (New, Rename, Delete)
  // ================================
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };
  
  // ================================
  // Handle menu actions
  // ================================
const handleAction = (action: string) => {
  if (action === "New Folder") {
  const newFolder: FileItem = {
    id: Date.now().toString() + Math.random(),
    name: "New Folder",
    type: "folder",
    path: (breadcrumbs.map(b => b.name).join("/") + "/New Folder").replace("//", "/"),
    children: [],
    isVirtual: true, // mark as virtual
  };

  // Update root and current path
  const updatedRoot = addItemToCurrentPath(rootData, breadcrumbs, newFolder);
  setRootData(updatedRoot);
  setCurrentPath(
    breadcrumbs.length
      ? findItemById(updatedRoot, breadcrumbs[breadcrumbs.length - 1].id)?.children || []
      : updatedRoot
  );

  // ✅ Use setTimeout to ensure re-render before focusing input
  setSelectedItem(newFolder);
  setTimeout(() => {
    setEditingId(newFolder.id);
    setEditingName(newFolder.name);
  }, 0);
}

  if (action === "Rename") {
    if (!selectedItem) {
      alert("Please select an item to rename.");
      return;
    }

    // Start editing the selected item
    setEditingId(selectedItem.id);
    setEditingName(selectedItem.name);
  }

  if (action === "Delete") {
    if (!selectedItem) {
      alert("Please select an item to delete.");
      return;
    }

    const updatedRoot = deleteItemById(rootData, selectedItem.id);
    setRootData(updatedRoot);
    setCurrentPath(
      breadcrumbs.length
        ? findItemById(updatedRoot, breadcrumbs[breadcrumbs.length - 1].id)?.children || []
        : updatedRoot
    );

    fileActions.deleteFile(selectedItem);

    // Clear selection
    setSelectedItem(null);
    setEditingId(null);
    setEditingName("");
  }

  if (action === "Download") {
    if (!selectedItem) {
      alert("Please select an item to download.");
      return;
    }

    if (selectedItem.type === "folder") {
      fileActions.downloadFolder(selectedItem);
    } else {
      fileActions.downloadFile(selectedItem);
    }
  }

  handleClose(); // Close any open context menu
};

    
  // ================================
  // Handle rename action
  // ================================

 const handleRenameSubmit = async (id: string) => {
  if (!editingName || !selectedItem) return;

  // If virtual folder, just update locally
  if (selectedItem.isVirtual) {
    const updatedRoot = updateItemName(rootData, id, editingName);
    setRootData(updatedRoot);
    setCurrentPath(
      breadcrumbs.length
        ? findItemById(updatedRoot, breadcrumbs[breadcrumbs.length - 1].id)?.children || []
        : updatedRoot
    );

    setEditingId(null);
    setEditingName("");

    // Once renamed, it is no longer virtual if needed
    setSelectedItem(prev => prev ? { ...prev, isVirtual: false } : prev);
    return;
  }

  // Otherwise, rename on server
  const success = await fileActions.renameItem(selectedItem, editingName);
  if (success) {
    const updatedRoot = updateItemName(rootData, id, editingName);
    setRootData(updatedRoot);
    setCurrentPath(
      breadcrumbs.length
        ? findItemById(updatedRoot, breadcrumbs[breadcrumbs.length - 1].id)?.children || []
        : updatedRoot
    );
    setEditingId(null);
    setEditingName("");
  }
};



  // ================================
  // Drag & drop
  // ================================
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFilesUpload(e.dataTransfer.items);
  };

  // ================================
  // Handle file uploads
  // ================================
  
  const handleFilesUpload = async (filesOrItems: FileList | DataTransferItemList) => {
  setShowUploadPanel(true); // show the upload panel

  const uploadFiles: { file: File; path: string }[] = [];
  const allItems: FileItem[] = [];
  const basePath = breadcrumbs.map(b => b.name).join("/");

  // Recursive function to traverse directories
  const traverseFileTree = (entry: any, relativePath = ""): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (entry.isFile) {
        entry.file((file: File) => {
          const filePath = relativePath ? `${relativePath}/${file.name}` : file.name;
          const uploadPath = basePath ? `${basePath}/${filePath}` : filePath;

          // Add to flat upload list
          uploadFiles.push({ file, path: uploadPath });

          // Add to UI tree
          allItems.push({
            id: Date.now().toString() + Math.random(),
            name: file.name,
            type: "file",
            path: filePath,
            size: file.size,
            lastModified: file.lastModified.toString(),
          });

          resolve();
        }, reject);
      } else if (entry.isDirectory) {
        const folderPath = relativePath ? `${relativePath}/${entry.name}` : entry.name;
        const folderItem: FileItem = {
          id: Date.now().toString() + Math.random(),
          name: entry.name,
          type: "folder",
          path: folderPath,
          children: [],
        };
        allItems.push(folderItem);

        const reader = entry.createReader();
        reader.readEntries(async (entries: any[]) => {
          try {
            await Promise.all(entries.map(e => traverseFileTree(e, folderPath)));
            resolve();
          } catch (err) {
            reject(err);
          }
        });
      }
    });
  };

  // Collect all files/folders into flat array
  const traversePromises: Promise<void>[] = [];
  for (let i = 0; i < filesOrItems.length; i++) {
    const item = filesOrItems[i];

    if ("webkitGetAsEntry" in item && typeof item.webkitGetAsEntry === "function") {
      const entry = item.webkitGetAsEntry();
      if (entry) traversePromises.push(traverseFileTree(entry));
    } else {
      const file: File | null = item instanceof File ? item : item.getAsFile?.() || null;
      if (file) {
        const filePath = file.name;
        const uploadPath = basePath ? `${basePath}/${filePath}` : filePath;

        uploadFiles.push({ file, path: uploadPath });
        allItems.push({
          id: Date.now().toString() + Math.random(),
          name: file.name,
          type: "file",
          path: filePath,
          size: file.size,
          lastModified: file.lastModified.toString(),
        });
      }
    }
  }

  // Wait for all files/folders to be collected
  await Promise.all(traversePromises);

  // Update UI tree and current path
  setRootData(prev => addItemsToCurrentPath(prev, breadcrumbs, allItems));
    const updatedRoot = addItemsToCurrentPath(rootData, breadcrumbs, allItems);
  setRootData(updatedRoot);

  const parentFolder = breadcrumbs.length
    ? findItemById(updatedRoot, breadcrumbs[breadcrumbs.length - 1].id)?.children || []
    : updatedRoot;
    setCurrentPath(parentFolder);
    
    if (breadcrumbs.length) {
  // Start from immediate parent and go up
  let currentId = breadcrumbs[breadcrumbs.length - 1].id;

  while (currentId != "") {
    const folder = findItemById(updatedRoot, currentId);
    if (!folder || !folder.isVirtual) break;

    folder.isVirtual = false;
    currentId = folder.parentId ?? ""; // move to parent folder, set to "" if undefined
  }
}

  // Upload all files in parallel with progress tracking
  await Promise.all(
    uploadFiles.map(({ file, path }) =>
      fileService.uploadPresigned(file, path, (percent) => {
        setUploadProgress(prev => ({ ...prev, [path]: percent }));
      })
    )
  );

  // Hide panel after short delay
  setTimeout(() => {
    setUploadProgress({});
    setShowUploadPanel(false);
  }, 1000);
};


  // ================================
  // Flattern files
  // ================================

  const flattenFileItems = (item: FileItem): FileItem[] => {
  if (item.type === "file") return [item];
  if (item.children) return item.children.flatMap(flattenFileItems);
  return [];
  };

    // ================================
  // Mark folder as real.
  // ================================
  const markVirtualFoldersAsReal = (root: FileItem[], folderId: string) => {
  const folder = findItemById(root, folderId);
  if (!folder) return;

  if (folder.isVirtual) folder.isVirtual = false;

  // Check parent breadcrumb chain recursively
  if (folder.parentId) {
    markVirtualFoldersAsReal(root, folder.parentId);
  }
};
  
  //===============================
  //Logout
  //===============================
  const logout = () => {
    LoginService.logout();
  }


  // ================================
  // Double click to open folder
  // ================================
  const handleDoubleClick = async (item: FileItem) => {
    travelThroughFolder(item);
  };
  
  const travelThroughFolder = async (item: FileItem) => {
  if (item.type !== "folder") return;

  // Fetch files for this folder from API
  const data = await fileService.list(item.path);

  // Normalize API response to FileItem[]
  const normalized = normalizeFiles(data, item.id);

  // Update children of the clicked folder
  const updateChildren = (items: FileItem[]): FileItem[] =>
    items.map((i) => {
      if (i.id === item.id) return { ...i, children: normalized };
      if (i.children) return { ...i, children: updateChildren(i.children) };
      return i;
    });

  const updatedRoot = updateChildren(rootData);
  setRootData(updatedRoot);

  // Update right-side view
  setCurrentPath(normalized);

  // Update breadcrumbs
  const newBreadcrumbs = buildBreadcrumbs(item.id, rootData);
  setBreadcrumbs(newBreadcrumbs);
};


  // ================================
  // F2 key for renaming
  // ================================
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "F2" && selectedItem) {
        e.preventDefault();
        setEditingId(selectedItem.id);
        setEditingName(selectedItem.name);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedItem]);

  // ================================
  // Render tree (sidebar)
  // ================================
const renderTree = (items: FileItem[]): React.ReactNode =>
  items
    .filter((i) => i.type === "folder")
    .map((item) => (
      <TreeItem
        key={item.id}
        itemId={item.id}
        label={
          <span>
            <FolderIcon sx={{ color: "#FBC02D" }} fontSize="small" /> {item.name}
          </span>
        }
        onClick={async (e) => {
          e.stopPropagation(); // prevent event bubbling
          await travelThroughFolder(item); // fetch folder content and update right panel
        }}
      >
        {item.children && item.children.length > 0 ? renderTree(item.children) : null}
      </TreeItem>
    ));



  return (
    <div className="explorer-dashboard">
      <header className="explorer-header">
        <div className="header-left">
          <img src="/theekkathir.png" alt="Theekkathir"></img>
        </div>
        <div className="header-right">
          <span>Welcome, User</span>
          <button className="logout-btn" onClick={() => logout()}>
            Logout
          </button>
        </div>
      </header>

      <div className="explorer-body">
        {/* Sidebar */}
        <aside className="explorer-sidebar">
          <h3>Directories</h3>
          <SimpleTreeView
            slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
            onItemClick={async (event, nodeId) => {
              // Find the clicked item by nodeId in your rootData
              const findItemById = (items: FileItem[], id: string): FileItem | null => {
                for (const item of items) {
                  if (item.id === id) return item;
                  if (item.children) {
                    const found = findItemById(item.children, id);
                    if (found) return found;
                  }
                }
                return null;
              };

              const item = findItemById(rootData, nodeId);
              if (!item) return;

              // Call your async function to fetch the folder contents
              await travelThroughFolder(item);
            }}
          >
            {renderTree(rootData)}
          </SimpleTreeView>
        </aside>

        {/* Main */}
        <main
          className="explorer-main"
          onClick={(e) => e.target === e.currentTarget && setSelectedItem(null)}
          onContextMenu={(e) => e.target === e.currentTarget && handleContextMenu(e)}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true); // Optional: show drag highlight
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);

            // Call generic upload handler
            handleFilesUpload(e.dataTransfer.items);
          }}
        >
          {/* Breadcrumbs */}
          <div className="breadcrumbs">
            <span onClick={() => handleBreadcrumbClick(-1)}>Root</span>
            {breadcrumbs.map((crumb, idx) => (
              <span key={crumb.id} onClick={() => handleBreadcrumbClick(idx)}>
                {" > "} {crumb.name}
              </span>
            ))}
          </div>

          {/* File grid */}
          <div className="file-grid">
            {currentPath.map((item) => (
              <div
                key={item.id}
                className={`file-item ${selectedItem?.id === item.id ? "selected" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedItem(item);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  handleDoubleClick(item);
                }}
                onContextMenu={(e) => {
                  e.stopPropagation();
                  handleContextMenu(e, item);
                }}
              >
                <div className="file-icon">{item.type === "folder" ? "📁" : "📄"}</div>
                {editingId === item.id ? (
                  <TextField
                    size="small"
                    autoFocus
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={() => handleRenameSubmit(item.id)}
                    onKeyDown={(e) => e.key === "Enter" && handleRenameSubmit(item.id)}
                  />
                ) : (
                  <div className="file-name">{item.name}</div>
                )}
              </div>
            ))}
          </div>
        </main>
      </div>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        PaperProps={{
          onContextMenu: (e: React.MouseEvent<HTMLDivElement>) => e.preventDefault(),
        }}
      >
        {selectedItem ? (
          <>
            <MenuItem onClick={() => handleAction("Download")}>Download</MenuItem>
            <MenuItem onClick={() => handleAction("Rename")}>Rename</MenuItem>
            <MenuItem onClick={() => handleAction("Delete")}>Delete</MenuItem>
          </>
        ) : (
          <>
              <MenuItem onClick={() => {
                handleClose();      // close menu first
                setTimeout(() => {   // trigger file dialog after menu is closed
                  triggerFileUpload();
                }, 0);
              }}>Upload</MenuItem>
              <MenuItem onClick={() => handleAction("New Folder")}>New Folder</MenuItem>
          </>
        )}
      </Menu>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFilesUpload(e.target.files!)}
      />
      {showUploadPanel && (
      <Paper
        elevation={3}
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          width: 300,
          maxHeight: 400,
          overflowY: "auto",
          padding: "12px",
          zIndex: 2000,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1">Uploading files</Typography>
          <IconButton size="small" onClick={() => setShowUploadPanel(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {Object.entries(uploadProgress).map(([fileName, percent]) => (
          <Box key={fileName} mb={1}>
            <Typography variant="body2" noWrap>{fileName}</Typography>
            <LinearProgress variant="determinate" value={percent} />
          </Box>
        ))}
      </Paper>
    )}  
    </div>
  );
};

export default ExplorerDashboard;
